import { normalizeRawAnswer, buildDisplayAnswer } from './formDisplayUtils';

/**
 * mdtPlanUtils.js — pure helpers for the MDT Plan page (Semble MDT Report,
 * Well Life Clinic).
 *
 * The MDT Plan page (src/pages/providers/8_diagnosticDecision/4_MDTPlan.js)
 * lets the clinician flag "Add to plan" items at the end of the
 * Diagnostic Decision.  The selectable items are NOT hard-coded: they are
 * derived from the patient's own PAD schema copy
 * (dataModel.5_postAssessmentDiscussion), so the labels and ordering exactly
 * match what the PAD pages will later show, and any org-specific item
 * filtering applied when the patient document was created is respected
 * automatically.
 *
 * Kept as pure functions (no React, no Firebase) so the derivation logic can
 * be unit-tested directly — mirroring the Python counterpart
 * (single_report/semble_mdt_document._resolve_selected_titles) that resolves
 * the stored selections back to display titles at render time.
 */

/**
 * Build the selectable item groups for one PAD section.
 *
 * @param {Object|undefined} padStage - dataModel.5_postAssessmentDiscussion
 * @param {string} sectionId - PAD section id ('2' = Strategies and
 *   Recommendations, '3' = Treatment Options)
 * @param {string[]} subsectionIds - which subsections to include, in display
 *   order (e.g. ['1', '2', '3'] for Disclosure / Lifestyle / Environmental)
 * @returns {Array<{title: string, items: Array<{title: string, field: string, tooltipText: string}>}>}
 *   One group per non-empty subsection.  Items are sorted by questionNumber
 *   and keyed by the stable firestoreFieldName (selections are stored against
 *   the field name, not the display title, so later label edits cannot orphan
 *   stored data).  Only selectMultiple line items are included — free-text
 *   and follow-up questions are PAD-time concerns, not plan items.
 *   Group titles prefer subsectionTitleDisplay (same label the PAD pages show).
 */
export const buildMdtPlanItemGroups = (padStage, sectionId, subsectionIds) => {
  const section = padStage?.sections?.[sectionId];
  if (!section) return [];

  return subsectionIds
    .map((subsectionId) => {
      const subsection = section.subsections?.[subsectionId];
      if (!subsection) return null;
      const items = Object.values(subsection.questions || {})
        .filter((q) => q?.questionType === 'selectMultiple' && q.questionTitle && q.firestoreFieldName)
        .sort((a, b) => (a.questionNumber || 0) - (b.questionNumber || 0))
        .map((q) => ({
          title: q.questionTitle,
          field: q.firestoreFieldName,
          tooltipText: q.tooltipText || '',
        }));
      if (items.length === 0) return null;
      return {
        title: subsection.subsectionTitleDisplay || subsection.subsectionTitle,
        items,
      };
    })
    .filter(Boolean);
};

/**
 * PAD section heading metadata for the MDT Plan page panels.
 * Section numbers/titles come from the patient's own PAD schema copy so the
 * Strategies and Treatment headings match DIS/TRT exactly.
 */
export const getPadSectionMeta = (padStage, sectionId) => {
  const section = padStage?.sections?.[sectionId];
  return {
    sectionNumber: section?.sectionNumber ?? sectionId,
    sectionTitle: section?.sectionTitle || '',
  };
};

/**
 * Metadata for the Patient's Goals subsection (PAD section 1, subsection 2).
 * The MDT Plan goals panel reuses the same labels, tooltip, and notes subtitle
 * as REV.js so clinicians see identical wording before and during the PAD.
 */
export const getGoalsSubsectionMeta = (padStage) => {
  const subsection = padStage?.sections?.['1']?.subsections?.['2'];
  return {
    subsectionTitleDisplay: subsection?.subsectionTitleDisplay || "1.2 Patient's Goals",
    textBoxSubtitle: subsection?.textBoxSubtitle || "Capture details of patient's goals here:",
    notesRequired: subsection?.notesRequired !== false,
    tooltipText: subsection?.tooltipText || '',
  };
};

/** Service-user variable for intake assessment goals (Your Current Concerns form). */
export const ASSESSMENT_GOALS_SERVICE_USER_VAR = 'assessmentGoals';

/**
 * Locate the Clinical Interview "Assessment goals" prefill question.
 *
 * The canonical path is CI section 2 / subsection 1 / question 4, but on SLaM
 * Path 1 (CI Review → PAD in the same appointment) the clinician may never
 * open Presenting Complaint, so that Firestore node might not exist yet. We
 * therefore fall back to scanning all CI subsections for providerVarName
 * `assessmentGoals` so the PAD can still surface the patient's intake answer.
 *
 * @param {Object|undefined} dataModel - patientDataModel.dataModel
 * @returns {Object|null}
 */
export function findCiAssessmentGoalsQuestion(dataModel) {
  const fastPath =
    dataModel?.['2_clinicalInterview']?.sections?.['2']?.subsections?.['1']
      ?.subsectionQuestions?.['4'];
  if (fastPath) {
    return fastPath;
  }

  const ciSections = dataModel?.['2_clinicalInterview']?.sections || {};
  for (const section of Object.values(ciSections)) {
    for (const subsection of Object.values(section?.subsections || {})) {
      for (const question of Object.values(subsection?.subsectionQuestions || {})) {
        if (question?.providerVarName === ASSESSMENT_GOALS_SERVICE_USER_VAR) {
          return question;
        }
      }
    }
  }
  return null;
}

/**
 * Read the structured raw answer for assessment goals from the service-user
 * "Your Current Concerns" form (same source REV.js / MDT Plan use).
 *
 * @param {Object|undefined} formData - patientDataModel.formData
 * @returns {*}
 */
export function readRawAssessmentGoalsFromFormData(formData) {
  const questions = formData?.yourCurrentConcerns?.formSubsections?.[1]?.questions;
  if (!questions) return null;

  for (const question of Object.values(questions)) {
    for (const [subKey, subquestion] of Object.entries(question.subquestions || {})) {
      if (Number.isNaN(parseInt(subKey, 10))) continue;
      if (subquestion.serviceUserVarName === ASSESSMENT_GOALS_SERVICE_USER_VAR) {
        return normalizeRawAnswer(subquestion.answer, subquestion.otherAnswer) ?? null;
      }
    }
  }
  return null;
}

/**
 * Resolve the read-only Assessment goals row for PAD / MDT Plan.
 * Prefers the formatted CI patientPrefill when present; otherwise formats the
 * raw service-user answer so Path 1 clinicians still see intake goals.
 *
 * @param {{ dataModel?: Object, formData?: Object }} params
 * @returns {{ ciQuestion: Object|null, rawAnswer: *, displayText: string, hasPatientSelfReport: boolean }}
 */
export function resolveAssessmentGoalsDisplay({ dataModel, formData }) {
  const ciQuestion = findCiAssessmentGoalsQuestion(dataModel);
  const rawAnswer = readRawAssessmentGoalsFromFormData(formData);
  const prefill = (ciQuestion?.patientPrefill || '').trim();
  const formattedFromForm =
    rawAnswer != null && rawAnswer !== '' ? buildDisplayAnswer(rawAnswer) : '';
  const displayText = prefill || formattedFromForm || '';
  const hasPatientSelfReport = Boolean(prefill || formattedFromForm);

  return {
    ciQuestion,
    rawAnswer,
    displayText,
    hasPatientSelfReport,
  };
}

/**
 * The patient's own pre-appointment assessment goals, captured during the
 * Clinical Interview.  Shown read-only on the MDT Plan page for reference
 * (the same prefill the PAD's Patient's Goals subsection and the Semble goals
 * paragraph generation use as their fallback source).
 *
 * @param {Object|undefined} dataModel - patientDataModel.dataModel
 * @returns {string} the prefill text, or '' when absent
 */
export const getAssessmentGoalsPrefill = (dataModel) =>
  findCiAssessmentGoalsQuestion(dataModel)?.patientPrefill || '';

/**
 * Resolve which clinician-goals text the PAD Review page (and the PAD
 * goals paragraph) should use.
 *
 * The MDT Plan page can capture goals notes before the PAD happens. The
 * Review page shows those notes in the Patient's Goals box, but they only
 * become PAD data — which is what generation reads — once they are written
 * to `5_postAssessmentDiscussion.sections.1.subsections.2.clinicianNotes`.
 *
 * Distinction that callers must honour:
 *   - `null` / `undefined` / missing  → never written on PAD. Fall back to
 *     MDT Plan notes when those exist.
 *   - `''` (explicit empty string)    → the clinician cleared the PAD box.
 *     Do NOT fall back; they chose to drop the MDT prefill.
 *   - any other string                → PAD notes win (clinician edited or
 *     accepted the prefill).
 *
 * @param {{ padNotes?: string|null, mdtPlanNotes?: string|null }} params
 * @returns {{ value: string, source: 'pad'|'mdtPlan'|null }}
 */
export function resolvePadGoalsClinicianNotes({ padNotes, mdtPlanNotes } = {}) {
  // Explicit string (including '') means the PAD field has been written.
  if (typeof padNotes === 'string') {
    return { value: padNotes, source: 'pad' };
  }

  const mdt = typeof mdtPlanNotes === 'string' ? mdtPlanNotes : '';
  if (mdt.trim()) {
    return { value: mdt, source: 'mdtPlan' };
  }

  return { value: '', source: null };
}

/**
 * Stable field names for the synthetic "Treatment Planning" panel on the MDT
 * Plan page.  These are NOT derived from the PAD schema — they are high-level
 * strategy toggles meaning "discuss pharmacological / non-pharmacological
 * options in the PAD".  Selections are stored in toDiscussStrategies alongside
 * the regular Disclosure / Lifestyle / Environmental items and rendered in the
 * Semble report under a "Treatment Planning" heading.
 */
export const TREATMENT_PLANNING_PHARMACOLOGICAL_FIELD = 'treatmentPlanningPharmacological';
export const TREATMENT_PLANNING_NON_PHARMACOLOGICAL_FIELD = 'treatmentPlanningNonPharmacological';

/**
 * Resolve the "effective" set of selected medications for the plan.
 *
 * Individual medication selections (toDiscussTreatments) are gated behind the
 * high-level "Pharmacological Options" toggle (toDiscussStrategies
 * .treatmentPlanningPharmacological).  On the MDT Plan page the Medication
 * Planning panel is greyed out and disabled when that toggle is off, but the
 * per-medication selections are intentionally KEPT in Firestore so they
 * reappear if the clinician re-enables pharmacological planning.
 *
 * The catch: every consumer that turns selections into report content
 * (paragraph generation, orphan cleanup, the rendered medication list) must
 * treat medications as UNSELECTED whenever the pharmacological toggle is off —
 * otherwise a clinician who unticks "Pharmacological Options" after generating
 * the report still sees the old Medication Planning paragraphs in the final
 * document.  This helper is the single source of truth for that rule so the
 * frontend and backend stay in lock-step (mirrors the Python
 * get_effective_selected_treatments in semble_mdt_document.py).
 *
 * @param {Object|undefined} mdtPlan - dataModel.4_diagnosticDecision.mdtPlan
 *   (shape: { toDiscussStrategies, toDiscussTreatments, ... }).  May also be
 *   passed the two maps split out via an explicit { toDiscussStrategies,
 *   toDiscussTreatments } object.
 * @returns {Object<string, boolean>} the medications map when pharmacological
 *   planning is enabled, otherwise an empty object.
 */
export const getEffectiveSelectedTreatments = (mdtPlan) => {
  const strategies = mdtPlan?.toDiscussStrategies || {};
  if (strategies[TREATMENT_PLANNING_PHARMACOLOGICAL_FIELD] !== true) return {};
  return mdtPlan?.toDiscussTreatments || {};
};

/**
 * The fixed Treatment Planning group shown at the end of the Strategies section
 * on the MDT Plan page (two line items, no per-item tooltips).
 */
export const buildTreatmentPlanningGroup = () => ({
  title: 'Treatment Planning',
  items: [
    { title: 'Pharmacological Options', field: TREATMENT_PLANNING_PHARMACOLOGICAL_FIELD, tooltipText: '' },
    { title: 'Non-Pharmacological Options', field: TREATMENT_PLANNING_NON_PHARMACOLOGICAL_FIELD, tooltipText: '' },
  ],
});
